import React, { useCallback, useMemo, useState } from 'react';
import { Input } from 'reactstrap';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';
import { ChecklistEditorContext, ChecklistEditorDispatchContext } from '../checklist-editor-context';
import { Actions } from '../checklist-editor-reducer';
import { ChoiceQuestionOption } from './ChoiceQuestionOption';
import { useContextSelector } from 'use-context-selector';

const dndModifiers = [ restrictToVerticalAxis ];

export function ChoiceQuestion({
    questionId,
    listTag: ListTag,
    showIcon,
    addItemIcon: AddItemIcon,
    listItemIcon,
}) {
    const dispatch = useContextSelector(ChecklistEditorDispatchContext, state => state);
    const optionOrder = useContextSelector(ChecklistEditorContext, state => state.optionOrderByQuestionId[questionId]);

    const [ isDirty, setIsDirty ] = useState(false);
    const [ draftLabel, setDraftLabel ] = useState('');

    const renameDraftLabel = (e) => {
        const value = e.target?.value ?? '';
        if (value === draftLabel) {
            return;
        }

        setDraftLabel(value);
        setIsDirty(true);
    };

    const createOption = () => {
        if (!isDirty) {
            return;
        }

        const nextDraftLabel = draftLabel.trim();
        if (nextDraftLabel !== draftLabel) {
            setDraftLabel(draftLabel);
        }

        // Discard empty option.
        if ('' === nextDraftLabel) {
            setIsDirty(false);
            return;
        }

        dispatch({
            type: Actions.CREATE_OPTION,
            payload: {
                questionId,
                label: nextDraftLabel,
            },
        });

        setDraftLabel('');
        setIsDirty(false);
    };

    const addOptionOnKeyNavigation = (e) => {
        if (![ 'Enter', 'Tab' ].includes(e.key)) {
            return;
        }

        e.preventDefault();
        createOption();
    };

    const sortOptions = useCallback(({
        active,
        over,
    }) => {
        if (!over || active.id === over.id) {
            return;
        }

        const from = optionOrder.indexOf(active.id);
        const to = optionOrder.indexOf(over.id);

        dispatch({
            type: Actions.SORT_OPTIONS,
            payload: {
                questionId,
                from,
                to,
            },
        });
    }, [ optionOrder, questionId ]);

    const renderedListOfOptions = useMemo(() => optionOrder.map((optionId, optionIndex) => (
        <ChoiceQuestionOption
            key={ `question-${ questionId }-option-${ optionId }` }
            showIcon={ showIcon }
            listItemIcon={ listItemIcon }
            optionId={ optionId }
            questionId={ questionId }
            optionIndex={ optionIndex }
        />
    )), [ optionOrder, questionId ]);

    return (<ListTag className={ 'checklist-editor__choice-list' }>
        <DndContext
            onDragEnd={ sortOptions }
            modifiers={ dndModifiers }
            collisionDetection={ closestCenter }
        >
            <SortableContext items={ optionOrder }>
                { renderedListOfOptions }
            </SortableContext>
        </DndContext>
        <li className={ 'checklist-editor__choice-item checklist-editor__choice-item--new' }>
            <span className={ 'checklist-editor__choice-icon' }>
                { showIcon && AddItemIcon && <AddItemIcon /> }
            </span>
            <Input
                name={ 'option' }
                type={ 'text' }
                value={ draftLabel }
                placeholder={ 'Enter option text...' }
                onKeyDown={ addOptionOnKeyNavigation }
                onChange={ renameDraftLabel }
                onBlur={ createOption }
            />
        </li>
    </ListTag>);
}
